import * as React from 'react';
import { FileStatus } from '../../../FileStatus';
import { ProcessingTooltipProps } from '../../../ProcessingTooltip';
import './LabelStatus.scss';

/** What the status reads off a tree item. */
export type LabelStatusItem = {
  fileData?: {
    status?: { code?: string };
    processing_stage?: ProcessingTooltipProps['stage'];
    stage_updated_at?: string | null;
    queued_at?: string | null;
    files_ahead?: number | null;
    typical_processing_seconds?: number | null;
  };
  folderData?: { status?: string };
};

export type LabelStatusProps = {
  item?: LabelStatusItem;
};

/** The statuses worth a mark; done is what most of the tree is. */
const SHOWN = new Set(['processing', 'warning', 'failed']);

/**
 * A row's status, as the icon after its name: the spinner while its file is
 * processing (its tooltip says how far along), or the warning or failure mark.
 * Nothing once done: the rail down the row's edge already says so.
 */
export const LabelStatus: React.FC<LabelStatusProps> = ({ item }) => {
  const status =
    item?.fileData?.status?.code || item?.folderData?.status || null;

  /* What the processing spinner says on hover. Memoised on the fields so
     FileStatus, which is memoised, still skips rows whose file is unchanged. */
  const fileData = item?.fileData;
  const processing = React.useMemo(
    () => ({
      stage: fileData?.processing_stage,
      stageUpdatedAt: fileData?.stage_updated_at,
      queuedAt: fileData?.queued_at,
      filesAhead: fileData?.files_ahead,
      typicalSeconds: fileData?.typical_processing_seconds,
    }),
    [
      fileData?.processing_stage,
      fileData?.stage_updated_at,
      fileData?.queued_at,
      fileData?.files_ahead,
      fileData?.typical_processing_seconds,
    ]
  );

  if (!status || !SHOWN.has(status)) return null;

  return (
    <span className="tree-label-status" data-status={status}>
      <FileStatus status={status} processing={processing} />
    </span>
  );
};
